/**
 * El seguimiento de una campaña (28/10/2026) en las pantallas de siempre:
 * - `RenglonSeguimiento`: en el detalle, «Seguimiento de <original>» en la hija y «Seguimiento: <hija>» en la original,
 *   con el link a la otra. Lo ven el equipo y el cliente (es leer lo de su comercio).
 * - `AudienciaSeguimiento`: el paso Audiencia del asistente de la hija. La audiencia la fija la original y no se edita:
 *   se muestra el criterio y cuántos quedan HOY (la misma cuenta de la pantalla «Hacer un seguimiento»); al confirmar
 *   se vuelve a calcular.
 */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CornerDownRight, Loader2 } from "lucide-react";
import { oficialApi } from "../api";
import type { CampanaResumen, OpcionesDeSeguimiento } from "../types";
import { leerError, leerRespuesta, textoError } from "../logic/respuesta";
import { desgloseSeguimiento, rutaCampana } from "../logic/seguimiento";

export function RenglonSeguimiento({ campana }: { campana: Pick<CampanaResumen, "seguimientoDe" | "seguimiento"> }) {
  const de = campana.seguimientoDe;
  const hija = campana.seguimiento;
  if (!de && !hija) return null;
  return (
    <div className="mt-1 space-y-0.5 text-xs text-slate-500">
      {de && (
        <p className="flex items-center gap-1.5">
          <CornerDownRight size={12} className="flex-shrink-0 text-violet-500" aria-hidden="true" />
          <span>
            Seguimiento de <Link to={rutaCampana(de.campanaId)} className="font-semibold text-violet-600 hover:underline">{de.nombre}</Link>
            {de.texto && <span className="text-slate-400"> · {de.texto}</span>}
          </span>
        </p>
      )}
      {hija && (
        <p className="flex items-center gap-1.5">
          <CornerDownRight size={12} className="flex-shrink-0 text-violet-500" aria-hidden="true" />
          <span>
            Seguimiento: <Link to={rutaCampana(hija.campanaId)} className="font-semibold text-violet-600 hover:underline">{hija.nombre}</Link>
          </span>
        </p>
      )}
    </div>
  );
}

export function AudienciaSeguimiento({ seguimientoDe }: { seguimientoDe: NonNullable<CampanaResumen["seguimientoDe"]> }) {
  const [datos, setDatos] = useState<OpcionesDeSeguimiento | null>(null);
  const [error, setError] = useState("");
  const { campanaId, criterio, dias, sinBase, respondieron } = seguimientoDe;

  useEffect(() => {
    let vivo = true;
    setError("");
    (async () => {
      try {
        const l = leerRespuesta<OpcionesDeSeguimiento>(await oficialApi.opcionesSeguimiento(campanaId, { criterio, dias, sinBase, respondieron }));
        if (!vivo) return;
        if (!l.ok) setError(textoError(l));
        else setDatos(l.data);
      } catch (e) {
        if (vivo) setError(textoError(leerError(e)));
      }
    })();
    return () => { vivo = false; };
  }, [campanaId, criterio, dias, sinBase, respondieron]);

  const d = datos?.criterios?.[criterio];
  const el = datos?.elegido;
  return (
    <div className="space-y-3 rounded-xl border border-violet-100 bg-violet-50/40 p-4" data-testid="audiencia-seguimiento">
      <p className="flex items-center gap-1.5 text-sm text-slate-700">
        <CornerDownRight size={14} className="flex-shrink-0 text-violet-600" aria-hidden="true" />
        <span>Es el seguimiento de <Link to={rutaCampana(campanaId)} className="font-semibold text-violet-700 hover:underline">{seguimientoDe.nombre}</Link></span>
      </p>
      <p className="text-sm text-slate-700"><span className="font-semibold">A quiénes:</span> {seguimientoDe.texto}</p>
      {error ? (
        <p className="text-sm text-red-700" role="alert">No pudimos contar cuántos quedan. {error}</p>
      ) : !datos ? (
        <p className="text-sm text-slate-500 inline-flex items-center gap-1.5"><Loader2 size={13} className="animate-spin" /> Contando cuántos quedan…</p>
      ) : d && !d.disponible ? (
        <p className="text-sm text-amber-800" role="alert">{d.mensaje}{d.accion ? ` ${d.accion}` : ""}</p>
      ) : el ? (
        <div className="space-y-1">
          <p className="text-sm text-slate-800">
            Hoy quedan <strong className="text-lg text-slate-900">{el.pueden.toLocaleString("es-AR")}</strong> {el.pueden === 1 ? "persona" : "personas"}
            <span className="text-slate-500"> de {el.cumplen.toLocaleString("es-AR")} que {el.cumplen === 1 ? "cumple" : "cumplen"} el criterio.</span>
          </p>
          {desgloseSeguimiento(el, criterio, sinBase).length > 0 && (
            <p className="text-xs text-slate-500">Afuera: {desgloseSeguimiento(el, criterio, sinBase).join(" · ")}.</p>
          )}
        </div>
      ) : null}
      <p className="text-xs text-slate-500">
        La audiencia la fija la campaña original y no se cambia acá. Se vuelve a calcular al confirmar, con los datos de ese momento.
      </p>
    </div>
  );
}
